import React from 'react';
import {type IntlShape} from 'react-intl';
import {createFinancialCategoryTooltip as createTooltip} from './FinancialCategoryTooltip';
import {type TooltipFormulaItem} from '@/components/TooltipFormulaRenderer';
import {HelpCenterLinks} from '../links/HelpCenterLinks';

type FormatMessage = IntlShape['formatMessage'];

const EstimatedHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.estimated_hours'}),
		description: formatMessage({id: 'financial.task_breakdown.estimated_hours.tooltip.description'}),
	},
];

const ActualHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_hours'}),
		description: formatMessage({id: 'financial.task_breakdown.actual_hours.tooltip.description'}),
	},
];

const ActualBillableHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_billable_hours'}),
		description: formatMessage({id: 'financial.task_breakdown.actual_billable_hours.tooltip.description'}),
	},
];

const ActualNonBillableHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_non_billable_hours'}),
		description: formatMessage({id: 'financial.task_breakdown.actual_non_billable_hours.tooltip.description'}),
	},
];

const RemainingWorkHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_work_hours'}),
		description: formatMessage({id: 'financial.task_breakdown.remaining_work_hours.tooltip.description'}),
	},
];

const ProjectedTotalHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_hours'}),
		description: formatMessage({id: 'financial.task_breakdown.projected_total_hours.tooltip.description'}),
		details: [formatMessage({id: 'common.actual_hours'}), '+', formatMessage({id: 'common.remaining_work_hours'})],
	},
];

const EstimatedPointsTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.estimated_points'}),
		description: formatMessage({id: 'financial.task_breakdown.estimated_points.tooltip.description'}),
	},
];

const RemainingWorkPointsTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_work_points'}),
		description: formatMessage({id: 'financial.task_breakdown.remaining_work_points.tooltip.description'}),
	},
];

const EstimatedBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.estimated_billable_value_of_service'}),
		description: formatMessage({id: 'financial.task_breakdown.estimated_billable_value_of_service.tooltip.description'}),
		details: [formatMessage({id: 'common.estimated_billable_hours'}), 'x', formatMessage({id: 'common.rate'})],
	},
];

const ActualBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_billable_value_of_service'}),
		description: formatMessage({id: 'financial.task_breakdown.actual_billable_value_of_service.tooltip.description'}),
		details: [formatMessage({id: 'common.actual_billable_hours'}), 'x', formatMessage({id: 'common.rate'})],
	},
];

const RemainingWorkBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_work_billable_value_of_service'}),
		description: formatMessage({
			id: 'financial.task_breakdown.remaining_work_billable_value_of_service.tooltip.description',
		}),
		details: [formatMessage({id: 'common.remaining_work_billable_hours'}), 'x', formatMessage({id: 'common.rate'})],
	},
];

const ProjectedTotalBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_billable_value_of_service'}),
		description: formatMessage({
			id: 'financial.task_breakdown.projected_total_billable_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.actual_billable_value_of_service'}),
			'+',
			formatMessage({id: 'common.remaining_work_billable_value_of_service'}),
		],
	},
];

const EstimatedRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.estimated_revenue'}),
		description: formatMessage(
			{id: 'financial.task_breakdown.estimated_revenue.tooltip.description'},
			{link: <HelpCenterLinks.Revenue />}
		),
		details: [formatMessage({id: 'common.estimated_billable_hours'}), 'x', formatMessage({id: 'common.rate'})],
	},
];

const ActualRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_revenue'}),
		description: formatMessage(
			{id: 'financial.task_breakdown.actual_revenue.tooltip.description'},
			{link: <HelpCenterLinks.Revenue />}
		),
		details: [
			formatMessage({id: 'common.task_actual_billable_value_of_service_time'}),
			'x',
			'(',
			formatMessage({id: 'common.project_projected_total_revenue_time'}),
			'/',
			formatMessage({id: 'common.project_projected_total_billable_value_of_service_time'}),
			')',
		],
	},
];

const RemainingWorkRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_work_revenue'}),
		description: formatMessage(
			{id: 'financial.task_breakdown.remaining_work_revenue.tooltip.description'},
			{link: <HelpCenterLinks.Revenue />}
		),
		details: [
			formatMessage({id: 'common.task_remaining_work_billable_value_of_service_time'}),
			'x',
			'(',
			formatMessage({id: 'common.project_projected_total_revenue_time'}),
			'/',
			formatMessage({id: 'common.project_projected_total_billable_value_of_service_time'}),
			')',
		],
	},
];

const ProjectedTotalRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_revenue'}),
		description: formatMessage(
			{id: 'financial.task_breakdown.projected_total_revenue.tooltip.description'},
			{link: <HelpCenterLinks.Revenue />}
		),
		details: [formatMessage({id: 'common.actual_revenue'}), '+', formatMessage({id: 'common.remaining_work_revenue'})],
	},
];

const ProjectedOverUnderServicingTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_over_under_servicing'}),
		description: formatMessage({id: 'financial.task_breakdown.projected_over_under_servicing.tooltip.description'}),
		details: [
			formatMessage({id: 'common.projected_total_revenue'}),
			'-',
			formatMessage({id: 'common.projected_total_value_of_service'}),
		],
	},
];

export const TaskBreakdownAndReportHeadersTooltips = {
	EstimatedHoursTooltip: createTooltip(EstimatedHoursTooltipItems),
	ActualHoursTooltip: createTooltip(ActualHoursTooltipItems),
	ActualBillableHoursTooltip: createTooltip(ActualBillableHoursTooltipItems),
	ActualNonBillableHoursTooltip: createTooltip(ActualNonBillableHoursTooltipItems),
	RemainingWorkHoursTooltip: createTooltip(RemainingWorkHoursTooltipItems),
	ProjectedTotalHoursTooltip: createTooltip(ProjectedTotalHoursTooltipItems),
	EstimatedPointsTooltip: createTooltip(EstimatedPointsTooltipItems),
	RemainingWorkPointsTooltip: createTooltip(RemainingWorkPointsTooltipItems),
	EstimatedBillableValueOfServiceTooltip: createTooltip(EstimatedBillableValueOfServiceTooltipItems),
	ActualBillableValueOfServiceTooltip: createTooltip(ActualBillableValueOfServiceTooltipItems),
	RemainingWorkBillableValueOfServiceTooltip: createTooltip(RemainingWorkBillableValueOfServiceTooltipItems),
	ProjectedTotalBillableValueOfServiceTooltip: createTooltip(ProjectedTotalBillableValueOfServiceTooltipItems),
	EstimatedRevenueTooltip: createTooltip(EstimatedRevenueTooltipItems),
	ActualRevenueTooltip: createTooltip(ActualRevenueTooltipItems),
	RemainingWorkRevenueTooltip: createTooltip(RemainingWorkRevenueTooltipItems),
	ProjectedTotalRevenueTooltip: createTooltip(ProjectedTotalRevenueTooltipItems),
	ProjectedOverUnderServicingTooltip: createTooltip(ProjectedOverUnderServicingTooltipItems),
};
